import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IExhibitorPair } from "@evennt-tech/api-v2-core";
import { useExhibitor } from "hooks";
import { State } from "store/state";
import { CartProgress } from "store/exhibitors/cartStepper/helper";
import {
  goToCardDetails,
  goToPayment,
  selectBillingDetails,
  selectCartStepperStatus,
  updateBillingDetails,
} from "store/exhibitors/cartStepper/slice";
import { PaymentMethodType } from "ts/api/paymentMethod";
import {
  areBillingDetailsComplete,
  resolveBillingDetails,
} from "view/apps/Exhibitor/pages/Cart/components/Details/helpers";
import { isIdealPaymentFailedState } from "./idealPaymentFailed";

type Params = {
  hasProducts: boolean;
  exhibitorPair?: IExhibitorPair | null;
  paymentMethodType: PaymentMethodType;
  setPaymentMethodType: (type: PaymentMethodType) => void;
};

/**
 * Back from a failed iDEAL payment the page reloads with a fresh checkout
 * state, which would open the cart's first step. The billing details were
 * saved to the exhibitor pair before the redirect, so they are rebuilt from it
 * and the exhibitor lands on the payment method step with iDEAL selected.
 * Returns whether the failure message should show: while iDEAL is the chosen
 * method, until the exhibitor goes back to the cart.
 */
export const useResumeFailedIdealPayment = ({
  hasProducts,
  exhibitorPair,
  paymentMethodType,
  setPaymentMethodType,
}: Params): boolean => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore<State>();
  const exhibitor = useExhibitor();
  const { checkoutProgress } = useSelector(selectCartStepperStatus);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const isResumedRef = useRef(false);
  const isReturnedFromFailedPayment = isIdealPaymentFailedState(location.state);

  useEffect(() => {
    if (isResumedRef.current || !isReturnedFromFailedPayment || !hasProducts) {
      return;
    }
    if (!exhibitorPair || !exhibitor) {
      return;
    }
    isResumedRef.current = true;
    const billingDetails = selectBillingDetails(store.getState());
    const resumedBillingDetails = {
      ...billingDetails,
      ...resolveBillingDetails(billingDetails, exhibitorPair, exhibitor),
    };

    dispatch(updateBillingDetails(resumedBillingDetails));
    dispatch(goToPayment());
    if (areBillingDetailsComplete(resumedBillingDetails)) {
      dispatch(goToCardDetails());
    }
    setPaymentMethodType(PaymentMethodType.idealPayment);
    setIsPaymentFailed(true);
    // Consumed: a reload must not reopen the payment step.
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
  }, [
    isReturnedFromFailedPayment,
    hasProducts,
    exhibitorPair,
    exhibitor,
    store,
    dispatch,
    navigate,
    location.pathname,
    location.search,
    setPaymentMethodType,
  ]);

  const previousProgressRef = useRef(checkoutProgress);
  useEffect(() => {
    const isBackToCart =
      previousProgressRef.current !== CartProgress.CART &&
      checkoutProgress === CartProgress.CART;
    previousProgressRef.current = checkoutProgress;
    if (isBackToCart) {
      setIsPaymentFailed(false);
    }
  }, [checkoutProgress]);

  return (
    isPaymentFailed && paymentMethodType === PaymentMethodType.idealPayment
  );
};
